import { useState, useEffect, useMemo, Fragment } from 'react';
import api from '../../../hooks/useApi';
import Modal from '../../shared/Modal';
import {
  TASK_STATUSES, TASK_STATUS_LABELS, PHASES, DEP_TYPES, CONSTRAINT_TYPES,
  CONSTRAINT_MAP, fmtDY, fmtVariance,
} from './planConstants';
import { makeCalendar } from '../../../../../src/lib/schedule.js';
import { personDaysOnTask } from '../../../../../src/lib/capacity.js';
import { span, retime, pairError, predecessorClashes } from './taskDates';

/*
  One editor for every kind of row in the plan: a working task, a summary bar,
  or a zero-duration milestone.

  The three things Owen could not do before all live here: more than one person
  on a task (including an outside organisation), more than one predecessor, and
  a predecessor that is something other than finish-to-start with no lag.
*/

const EXTERNAL = '__external__';
const NEW_PARTY = '__new__';
// The picker's groups, in the order a plan most often needs them.
const PARTY_TYPES = [
  ['client', 'Clients'], ['contractor', 'Contractors'], ['supplier', 'Suppliers'], ['partner', 'Partners'], ['other', 'Other'],
];

export default function TaskEditor({
  task, tasks = [], people = [], externalParties = [], teams = [], resourceLines = [], holidays = [],
  onClose, onSave, onDelete, onCreateParty, onPartiesChanged, onSplit,
}) {
  const isNew = !task?.id;
  const cal = useMemo(() => makeCalendar({ holidays }), [holidays]);

  const [form, setForm] = useState(() => ({
    name: task?.name || '',
    description: task?.description || '',
    phase: task?.phase || 'execution',
    parent_task_id: task?.parent_task_id || '',
    team_id: task?.team_id || '',
    resource_line_id: task?.resource_line_id || '',
    status: task?.status || 'not_started',
    completion_pct: task?.completion_pct ?? 0,
    duration_days: task?.is_milestone ? 0 : (task?.duration_days ?? 1),
    duration_unit: task?.duration_unit || 'workdays',
    effort_hours: task?.effort_hours ?? '',
    effort_driven: task?.effort_driven ? true : false,
    start_date: task?.start_date || '',
    constraint_type: task?.constraint_type || 'asap',
    constraint_date: task?.constraint_date || '',
    is_summary: task?.is_summary ? true : false,
    is_milestone: task?.is_milestone ? true : false,
    task_type: task?.task_type || 'custom',
    checklist_template_id: task?.checklist_template_id || '',
  }));
  // Baseline and actual are each a start, finish and duration kept in step by
  // retime(). The forecast is the engine's: a start and a duration, with the
  // finish worked out from them.
  const [baseline, setBaseline] = useState(() => ({
    start: task?.baseline_start || '', finish: task?.baseline_finish || '',
    duration: task?.baseline_duration_days ?? '',
  }));
  const [actual, setActual] = useState(() => ({
    start: task?.actual_start || '', finish: task?.actual_finish || '',
    duration: span(cal, task?.actual_start, task?.actual_finish, task?.duration_unit) ?? '',
  }));
  const [predecessors, setPredecessors] = useState(() =>
    (task?.predecessors || []).map(p => ({
      predecessor_id: p.predecessor_id, dep_type: p.dep_type || 'FS',
      lag_days: p.lag_days ?? 0, lag_unit: p.lag_unit || 'workdays',
    })));
  // kind is carried on the row rather than inferred from which id is set, so a
  // half-filled external row does not flip back to a person picker.
  // ── Days of work ──
  // Everybody on a task is given days of work, the unit the rest of resourcing
  // uses. They are stored as planned hours (7.5 a day), which the load engine
  // spreads over the person's own working days. The share the scheduler needs
  // is worked out from them: days of work over the working days the person has
  // in the task's dates.
  const DAY = 7.5;
  const peopleById = useMemo(() => new Map(people.map(p => [p.id, p])), [people]);
  const availableDays = (a, start, finish) => {
    if (!start) return 0;
    const person = a.person_id ? peopleById.get(a.person_id) : null;
    if (person) return personDaysOnTask(person, start, finish || start, 1, cal);
    return Number(span(cal, start, finish || start, 'workdays')) || 0;
  };
  const r2 = (n) => Math.round(n * 100) / 100;
  const initialDays = (a) => {
    if (Number(a.planned_hours) > 0) return r2(Number(a.planned_hours) / DAY);
    const avail = availableDays(a, task?.start_date, task?.end_date);
    const share = (Number(a.fte_pct) || 100) / 100;
    return avail > 0 ? r2(avail * share) : r2((Number(task?.duration_days) || 0) * share);
  };
  const shareOf = (a, start, finish) => {
    const avail = availableDays(a, start, finish);
    const days = Number(a.days) || 0;
    return avail > 0 ? days / avail : null;
  };
  // A task with nobody on it: the work its team is asked for, in days.
  const [workDays, setWorkDays] = useState(() =>
    r2((Number(task?.duration_days) || 0) * ((Number(task?.fte_pct) || 100) / 100)));

  // Team allowances on a summary bar, edited in days (stored as hours).
  const [teamAllowances, setTeamAllowances] = useState(() =>
    (task?.team_allowances || []).map(a => ({ team_id: a.team_id, days: Math.round((Number(a.planned_hours) || 0) / 7.5 * 100) / 100 })));
  const [assignees, setAssignees] = useState(() =>
    (task?.assignees || []).map(a => ({
      kind: a.external_party_id ? 'party' : 'person',
      person_id: a.person_id || '', external_party_id: a.external_party_id || '',
      fte_pct: a.fte_pct ?? 100, is_primary: !!a.is_primary,
      planned_hours: a.planned_hours ?? '',
      days: '',
    })).map(a => ({ ...a, days: initialDays(a) })));
  const [checklistTemplates, setChecklistTemplates] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (form.task_type === 'checklist' && !checklistTemplates.length) {
      api.get('repeatable-templates').then(setChecklistTemplates).catch(() => {});
    }
  }, [form.task_type, checklistTemplates.length]);

  // You cannot depend on yourself, nor on anything inside you.
  const descendants = useMemo(() => {
    if (!task?.id) return new Set();
    const kids = new Map();
    for (const t of tasks) {
      if (!kids.has(t.parent_task_id)) kids.set(t.parent_task_id, []);
      kids.get(t.parent_task_id).push(t.id);
    }
    const out = new Set([task.id]);
    const walk = (id) => (kids.get(id) || []).forEach(c => { if (!out.has(c)) { out.add(c); walk(c); } });
    walk(task.id);
    return out;
  }, [tasks, task?.id]);

  const linkable = tasks.filter(t => !descendants.has(t.id));
  const parentable = tasks.filter(t => !descendants.has(t.id));

  const usedPredecessors = new Set(predecessors.map(p => p.predecessor_id));
  const isMilestone = form.is_milestone;
  const isSummary = form.is_summary || task?.has_children;

  const addPredecessor = () => {
    const first = linkable.find(t => !usedPredecessors.has(t.id));
    if (!first) return;
    setPredecessors(p => [...p, { predecessor_id: first.id, dep_type: 'FS', lag_days: 0, lag_unit: 'workdays' }]);
  };
  const setPred = (i, patch) => setPredecessors(p => p.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const removePred = (i) => setPredecessors(p => p.filter((_, j) => j !== i));

  const addAssignee = (kind) => setAssignees(a => [...a, {
    kind,
    person_id: kind === 'person' ? (people[0]?.id || '') : '',
    external_party_id: kind === 'party' ? (externalParties[0]?.id || '') : '',
    fte_pct: 100,
    planned_hours: '',
    days: '',
    is_primary: kind === 'person' && !a.some(x => x.person_id && x.is_primary),
  }]);
  const setAssignee = (i, patch) => setAssignees(a => a.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const removeAssignee = (i) => setAssignees(a => a.filter((_, j) => j !== i));
  // Exactly one lead, and it has to be a person: owner_id elsewhere in the app
  // points at a member of staff.
  const makePrimary = (i) => setAssignees(a => a.map((x, j) => ({ ...x, is_primary: j === i && !!x.person_id })));

  // ── External as a team ──
  // "External…" in the Team field opens a second picker of outside
  // organisations, and the one chosen there is the first external row under
  // Assigned to. An external task has no internal team, so it never shows as
  // that team's unstaffed demand.
  const [externalTeam, setExternalTeam] = useState(() =>
    !task?.team_id && (task?.assignees || []).some(a => a.external_party_id));
  const [newParty, setNewParty] = useState(null);
  const [partyError, setPartyError] = useState('');
  const [addingParty, setAddingParty] = useState(false);
  const teamPartyIndex = assignees.findIndex(a => a.kind === 'party');
  const teamPartyId = teamPartyIndex >= 0 ? assignees[teamPartyIndex].external_party_id : '';
  const typeOf = (p) => (PARTY_TYPES.some(([t]) => t === p.party_type) ? p.party_type : 'other');
  const partyGroups = PARTY_TYPES
    .map(([type, label]) => [label, externalParties.filter(p => typeOf(p) === type)])
    .filter(([, list]) => list.length);

  const chooseTeam = (v) => {
    if (v === EXTERNAL) { set('team_id', ''); setExternalTeam(true); return; }
    set('team_id', v);
    if (externalTeam && teamPartyIndex >= 0) removeAssignee(teamPartyIndex);
    setExternalTeam(false);
    setNewParty(null);
  };
  const chooseParty = (id) => {
    if (id === NEW_PARTY) { setNewParty({ name: '', short_code: '', party_type: 'contractor' }); setPartyError(''); return; }
    setNewParty(null);
    if (teamPartyIndex >= 0) setAssignee(teamPartyIndex, { external_party_id: id });
    else if (id) {
      setAssignees(a => [...a, {
        kind: 'party', person_id: '', external_party_id: id, fte_pct: 100, planned_hours: '', days: '', is_primary: false,
      }]);
    }
  };
  const addParty = async () => {
    setPartyError('');
    setAddingParty(true);
    try {
      const { id } = await api.post('external-parties', {
        name: newParty.name.trim(), short_code: newParty.short_code.trim() || null, party_type: newParty.party_type,
      });
      await onPartiesChanged?.();
      chooseParty(id);
    } catch (e) {
      setPartyError(e.message || 'Could not add that organisation.');
    } finally {
      setAddingParty(false);
    }
  };

  const allowanceTeams = teamAllowances.map(a => a.team_id).filter(Boolean);
  const valid = form.name.trim() && (form.task_type !== 'checklist' || form.checklist_template_id)
    && new Set(allowanceTeams).size === allowanceTeams.length;

  // The work / units / duration triangle, previewed live so the effect of
  // adding a person is visible before saving rather than after.
  const HOURS_PER_DAY = 7.5;
  const assignedUnits = assignees.filter(a => a.person_id || a.external_party_id)
    .reduce((s, a) => {
      const share = Number(a.days) > 0 ? shareOf(a, task?.start_date || form.start_date, task?.end_date) : null;
      return s + (share != null ? share : (parseInt(a.fte_pct, 10) || 100) / 100);
    }, 0);
  const units = assignedUnits > 0 ? assignedUnits : 1;
  const derivedEffort = Math.round((parseFloat(form.duration_days) || 0) * units * HOURS_PER_DAY * 100) / 100;
  const derivedDuration = Math.max(0.5,
    Math.round(((parseFloat(form.effort_hours) || 0) / (units * HOURS_PER_DAY)) * 2) / 2);

  // ── Dates ──
  // The engine pins a started task to its actual start, and a finished one to
  // its actual finish, so the forecast row shows the same.
  const unit = form.duration_unit;
  const today = new Date().toISOString().slice(0, 10);
  const startNote = isSummary ? null
    : actual.start ? 'follows the actual start'
    : predecessors.length ? 'set by its predecessors'
    : form.constraint_type === 'mso' ? 'set by the must-start-on date' : null;
  const forecastStart = isSummary ? form.start_date
    : actual.start || (form.constraint_type === 'mso' ? form.constraint_date : form.start_date);
  const forecastFinish = (isSummary ? task?.end_date
    : isMilestone ? forecastStart
    : actual.finish || cal.finishFromStart(forecastStart, form.effort_driven ? derivedDuration : form.duration_days, unit)) || '';
  const forecastDuration = isMilestone ? 0
    : isSummary || actual.finish ? (Math.max(0, span(cal, forecastStart, forecastFinish, unit) ?? 0) || '')
    : form.effort_driven ? derivedDuration : form.duration_days;
  const setForecast = (field, v) => {
    if (field === 'start') set('start_date', v);
    else if (field === 'duration') set('duration_days', v);
    else if (span(cal, forecastStart, v, unit) > 0) set('duration_days', span(cal, forecastStart, v, unit));
  };
  const setUnit = (u) => {
    set('duration_unit', u);
    // Recount the recorded rows in the new unit; their dates stay put.
    setBaseline(b => retime(b, 'start', b.start, cal, u));
    setActual(a => retime(a, 'start', a.start, cal, u));
  };
  const milestoneView = (r) => (isMilestone ? { ...r, finish: r.start, duration: 0 } : r);
  const lockAll = { start: true, finish: true, duration: true };
  const lockedLook = 'form-input disabled:opacity-50 disabled:cursor-not-allowed';
  const dateRows = [
    !isNew && {
      label: 'Baseline', ...milestoneView(baseline),
      lock: { start: false, finish: isMilestone, duration: isMilestone },
      on: (f, v) => setBaseline(b => retime(b, f, v, cal, unit)),
    },
    {
      label: 'Forecast', start: forecastStart || '', finish: forecastFinish, duration: forecastDuration,
      lock: isSummary ? lockAll : {
        start: !!startNote,
        finish: isMilestone || !!actual.finish || form.effort_driven,
        duration: isMilestone || !!actual.finish || form.effort_driven,
      },
      on: setForecast,
    },
    !isNew && {
      label: 'Actual', ...milestoneView(actual),
      lock: isSummary ? lockAll : { start: false, finish: isMilestone, duration: isMilestone || !actual.start },
      placeholder: actual.start && !actual.finish && actual.start <= today
        ? `${span(cal, actual.start, today, unit)} so far` : '',
      on: (f, v) => setActual(a => retime(a, f, v, cal, unit)),
    },
  ].filter(Boolean);
  const baselineError = !isNew && !isMilestone && pairError(baseline);
  const actualError = !isNew && !isMilestone && !isSummary && pairError(actual);
  const clashes = isSummary ? [] : predecessorClashes(actual.start, predecessors, tasks);

  // Status still stamps the actual dates, as the server does for callers that
  // send status alone. Doing it here means the predecessor check sees it.
  const stampActuals = (s) => {
    if (s === 'in_progress') {
      setActual(a => ({
        start: a.start || (form.start_date && form.start_date < today ? form.start_date : today),
        finish: '', duration: '',
      }));
    } else if (s === 'completed') {
      setActual(a => retime({ ...a, start: a.start || form.start_date || today }, 'finish', a.finish || today, cal, unit));
    } else if (s === 'not_started') {
      setActual({ start: '', finish: '', duration: '' });
    }
  };

  const handleSave = async () => {
    setError('');
    if (!valid) { setError('A task needs a name.'); return; }
    if (CONSTRAINT_MAP[form.constraint_type]?.needsDate && !form.constraint_date) {
      setError(`"${CONSTRAINT_MAP[form.constraint_type].label}" needs a date.`);
      return;
    }
    setSaving(true);
    try {
      await onSave({
        ...form,
        duration_days: isMilestone ? 0
          : form.effort_driven ? derivedDuration
          : Math.max(0, parseFloat(form.duration_days) || 1),
        effort_hours: form.effort_driven
          ? (parseFloat(form.effort_hours) || null)
          : derivedEffort,
        effort_driven: form.effort_driven ? 1 : 0,
        completion_pct: parseInt(form.completion_pct, 10) || 0,
        parent_task_id: form.parent_task_id || null,
        team_id: form.team_id || null,
        resource_line_id: form.resource_line_id || null,
        start_date: form.start_date || null,
        constraint_date: form.constraint_date || null,
        ...(isSummary ? {} : {
          actual_start: actual.start || null,
          actual_finish: (isMilestone ? actual.start : actual.finish) || null,
        }),
        ...(isNew ? {} : {
          baseline_start: baseline.start || null,
          baseline_finish: (isMilestone ? baseline.start : baseline.finish) || null,
          baseline_duration_days: isMilestone ? (baseline.start ? 0 : null)
            : (Number.isFinite(parseFloat(baseline.duration)) ? parseFloat(baseline.duration) : null),
        }),
        checklist_template_id: form.checklist_template_id || null,
        is_summary: form.is_summary ? 1 : 0,
        is_milestone: form.is_milestone ? 1 : 0,
        predecessors: predecessors.filter(p => p.predecessor_id),
        assignees: assignees.filter(a => a.person_id || a.external_party_id).map(a => {
          const days = Number(a.days) || 0;
          if (days <= 0) return { ...a, planned_hours: null };
          const share = shareOf(a, forecastStart, forecastFinish);
          return {
            ...a,
            planned_hours: Math.round(days * DAY * 100) / 100,
            fte_pct: share != null ? Math.min(100, Math.max(1, Math.round(share * 100))) : (a.fte_pct || 100),
          };
        }),
        // Nobody on it: the task asks its team for its work in days, as a share.
        ...(!isSummary && !isMilestone && !assignees.some(a => a.person_id || a.external_party_id)
          && Number(forecastDuration) > 0 && Number(workDays) > 0
          // Over 100% is more than one person's worth, which a team can be asked for.
          ? { fte_pct: Math.min(1000, Math.max(1, Math.round((Number(workDays) / Number(forecastDuration)) * 100))) }
          : {}),
        ...(isSummary ? {
          team_allowances: teamAllowances
            .filter(a => a.team_id && Number(a.days) > 0)
            .map(a => ({ team_id: a.team_id, planned_hours: Math.round(Number(a.days) * 7.5 * 100) / 100 })),
        } : {}),
      });
    } catch (e) {
      setError(e.message || 'Could not save.');
      setSaving(false);
    }
  };

  return (
    <Modal title={isNew ? 'Add Task' : 'Edit Task'} onClose={onClose} wide footer={
      <>
        {!isNew && onDelete && (
          <button onClick={() => onDelete(task)} className="px-3 py-1 text-[10px] border border-red-200 text-red-500 rounded bg-white mr-auto hover:bg-red-50">
            Delete
          </button>
        )}
        <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        <button onClick={handleSave} disabled={!valid || saving || !!baselineError || !!actualError}
                className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">
          {saving ? 'Saving…' : clashes.length ? 'Save anyway' : 'Save'}
        </button>
      </>
    }>
      <div className="space-y-3">
        {error && <div className="text-[9px] text-red-600 bg-red-50 border border-red-100 px-2 py-1.5 rounded">{error}</div>}

        {/* ── What kind of row is this ── */}
        <div className="flex items-center gap-4 pb-2 border-b border-gray-100">
          <span className="text-[8px] font-bold text-gray-500 uppercase">Row type</span>
          {[
            ['task', 'Task', !form.is_summary && !form.is_milestone],
            ['summary', 'Summary bar', form.is_summary && !form.is_milestone],
            ['milestone', 'Milestone', form.is_milestone],
          ].map(([id, label, on]) => (
            <label key={id} className="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" name="rowtype" checked={on} className="accent-navy"
                     onChange={() => {
                       set('is_summary', id === 'summary');
                       set('is_milestone', id === 'milestone');
                       if (id === 'milestone') set('duration_days', 0);
                       else if (!form.duration_days) set('duration_days', 1);
                     }} />
              <span className="text-[9px] font-semibold text-gray-700">{label}</span>
            </label>
          ))}
          {task?.has_children && (
            <span className="text-[8px] text-gray-400 ml-auto">
              This row has {task.child_count} task{task.child_count !== 1 ? 's' : ''} under it, so its dates come from them.
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Name *</label>
            <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)}
                   placeholder={isMilestone ? 'e.g. Glasgow site live' : 'e.g. Configure core switch'} autoFocus />
          </div>
          <div className="col-span-2">
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Description</label>
            <input className="form-input" value={form.description} onChange={e => set('description', e.target.value)} />
          </div>

          {/* ── Where it sits in the WBS ── */}
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Sits under</label>
            <select className="form-select" value={form.parent_task_id} onChange={e => set('parent_task_id', e.target.value)}>
              <option value="">— top level —</option>
              {parentable.map(t => (
                <option key={t.id} value={t.id}>{' '.repeat(t.depth * 2)}{t.wbs_code} {t.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">
              Phase <span className="font-normal normal-case text-gray-400">— a tag, not the structure</span>
            </label>
            <select className="form-select" value={form.phase} onChange={e => set('phase', e.target.value)}>
              {PHASES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>

          {/* ── Duration, effort, and which of the two drives the other ── */}
          {!isSummary && !isMilestone && (
            <>
              <div className="col-span-2 flex items-center gap-4">
                <span className="text-[8px] font-bold text-gray-500 uppercase">Schedule from</span>
                {[[false, 'Duration', 'Fix the elapsed time. Adding people raises the effort.'],
                  [true, 'Effort', 'Fix the hours. Adding people shortens the job.']].map(([val, label, hint]) => (
                  <label key={label} className="flex items-center gap-1.5 cursor-pointer" title={hint}>
                    <input type="radio" name="effortdriven" className="accent-navy"
                           checked={!!form.effort_driven === val}
                           onChange={() => set('effort_driven', val)} />
                    <span className="text-[9px] font-semibold text-gray-700">{label}</span>
                  </label>
                ))}
                <span className="text-[7.5px] text-gray-400 ml-auto">
                  {assignedUnits > 0
                    ? `${assignedUnits.toFixed(2).replace(/\.00$/, '')} resource${assignedUnits === 1 ? '' : 's'} assigned`
                    : 'nobody assigned yet, costed as one'}
                </span>
              </div>
              <div>
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">
                  Effort (hours) {!form.effort_driven && <span className="font-normal normal-case text-gray-400">— follows the duration</span>}
                </label>
                <input className="form-input" type="number" min="0" step="0.5"
                       disabled={!form.effort_driven}
                       value={form.effort_driven ? form.effort_hours : derivedEffort}
                       onChange={e => set('effort_hours', e.target.value)} />
                {task?.actual_work_hours > 0 && (
                  <div className={`text-[7.5px] mt-0.5 ${task.actual_work_hours > (task.effort_hours || 0) ? 'text-red-600' : 'text-gray-500'}`}>
                    {task.actual_work_hours}h booked so far
                    {task.effort_hours > 0 && ` (${Math.round((task.actual_work_hours / task.effort_hours) * 100)}% of the estimate)`}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── Scheduling ── */}
          {!isSummary && (
            <>
              <div>
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Scheduling</label>
                <select className="form-select" value={form.constraint_type} onChange={e => set('constraint_type', e.target.value)}>
                  {CONSTRAINT_TYPES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
                <div className="text-[7.5px] text-gray-400 mt-0.5">{CONSTRAINT_MAP[form.constraint_type]?.hint}</div>
              </div>
              {CONSTRAINT_MAP[form.constraint_type]?.needsDate && (
                <div>
                  <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Constraint date *</label>
                  <input className="form-input" type="date" value={form.constraint_date}
                         onChange={e => set('constraint_date', e.target.value)} />
                </div>
              )}
            </>
          )}

          {/* ── Status ── */}
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Status</label>
            <select className="form-select" value={form.status} disabled={isSummary}
                    onChange={e => {
                      const s = e.target.value;
                      set('status', s);
                      stampActuals(s);
                      if (s === 'completed') set('completion_pct', 100);
                      else if (s === 'not_started') set('completion_pct', 0);
                    }}>
              {TASK_STATUSES.map(s => <option key={s} value={s}>{TASK_STATUS_LABELS[s]}</option>)}
            </select>
            {isSummary && <div className="text-[7.5px] text-gray-400 mt-0.5">Rolled up from the tasks underneath.</div>}
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Team</label>
            <select className={`form-select ${externalTeam ? 'bg-amber-50' : ''}`} value={externalTeam ? EXTERNAL : form.team_id}
                    onChange={e => chooseTeam(e.target.value)}>
              <option value="">— none —</option>
              {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              {!isSummary && <option value={EXTERNAL}>External…</option>}
            </select>
            {externalTeam && (
              <div className="mt-1 space-y-1">
                <select className="form-select bg-amber-50" value={newParty ? NEW_PARTY : teamPartyId}
                        onChange={e => chooseParty(e.target.value)}>
                  <option value="">— choose organisation —</option>
                  {partyGroups.map(([label, list]) => (
                    <optgroup key={label} label={label}>
                      {list.map(p => <option key={p.id} value={p.id}>{p.short_code ? `${p.short_code} · ` : ''}{p.name}</option>)}
                    </optgroup>
                  ))}
                  <option value={NEW_PARTY}>+ New organisation…</option>
                </select>
                {newParty && (
                  <div className="border border-amber-300 bg-amber-50 rounded p-1.5 space-y-1">
                    <input className="form-input text-[9px]" value={newParty.name} placeholder="Name, e.g. Jones Lang LaSalle" autoFocus
                           onChange={e => setNewParty(n => ({ ...n, name: e.target.value }))} />
                    <div className="flex gap-1">
                      <input className="form-input text-[9px]" style={{ width: 64, flex: '0 0 auto' }} value={newParty.short_code}
                             placeholder="JLL" maxLength={8} title="Short code, shown on the bar"
                             onChange={e => setNewParty(n => ({ ...n, short_code: e.target.value }))} />
                      <select className="form-select text-[9px] flex-1" value={newParty.party_type}
                              onChange={e => setNewParty(n => ({ ...n, party_type: e.target.value }))}>
                        {PARTY_TYPES.map(([t, label]) => <option key={t} value={t}>{label.replace(/s$/, '')}</option>)}
                      </select>
                    </div>
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={addParty} disabled={addingParty || !newParty.name.trim()}
                              className="px-2 py-0.5 text-[9px] font-semibold bg-navy text-white rounded disabled:opacity-40">
                        {addingParty ? 'Adding…' : 'Add and use'}
                      </button>
                      <button type="button" onClick={() => { setNewParty(null); setPartyError(''); }}
                              className="px-2 py-0.5 text-[9px] text-gray-500 hover:text-navy">Cancel</button>
                    </div>
                    {partyError && <div className="text-[8px] text-red-600">{partyError}</div>}
                  </div>
                )}
                {!newParty && (
                  <div className="text-[7.5px] text-gray-400">
                    {isMilestone ? 'Shown in amber on the Gantt.' : 'Shown in amber on the Gantt. Their days go under Assigned to.'}
                  </div>
                )}
              </div>
            )}
          </div>
          {!isMilestone && (
            <div className="col-span-2">
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Resource line</label>
              <select className="form-select" value={form.resource_line_id} onChange={e => set('resource_line_id', e.target.value)}>
                <option value="">— not linked —</option>
                {resourceLines.map(l => (
                  <option key={l.id} value={l.id}>
                    {l.band_code}{l.band_name ? ` ${l.band_name}` : ''}{l.description ? ` · ${l.description}` : ''} ({Math.round((l.sold_days || 0) * 10) / 10}d sold)
                  </option>
                ))}
              </select>
              <div className="text-[7.5px] text-gray-400 mt-0.5">
                {resourceLines.length
                  ? 'The SOW line this work delivers. Its days count as planned against the line, and with nobody assigned, as unstaffed demand for its band.'
                  : 'This SOW has no resource lines yet.'}
              </div>
            </div>
          )}

          {!isSummary && (form.status === 'in_progress' || form.status === 'blocked') && (
            <div className="col-span-2">
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">% complete</label>
              <div className="flex items-center gap-3">
                <input type="range" min="0" max="100" step="5" value={form.completion_pct}
                       onChange={e => set('completion_pct', parseInt(e.target.value, 10))}
                       className={`flex-1 ${form.status === 'blocked' ? 'accent-red-500' : 'accent-blue-500'}`} />
                <span className="text-[10px] font-bold w-10 text-right">{form.completion_pct}%</span>
              </div>
              {task?.expected_pct != null && (
                <div className={`text-[7.5px] mt-0.5 ${(task.variance_pct ?? 0) < -5 ? 'text-red-600' : 'text-gray-500'}`}>
                  Straight-line, this should be {task.expected_pct}% by today.
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Dates: what was agreed, what we now expect, what happened ── */}
        <div className="border border-gray-200 rounded p-2">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[8px] font-bold text-gray-500 uppercase">Dates</span>
            {!isSummary && !isMilestone && (
              <div className="w-32">
                <select className="form-select text-[9px]" value={unit} onChange={e => setUnit(e.target.value)}
                        title="Calendar days run through weekends and bank holidays: drying, curing, a supplier's clock.">
                  <option value="workdays">working days</option>
                  <option value="calendar">calendar days</option>
                </select>
              </div>
            )}
          </div>
          <div className="grid grid-cols-[4rem_1fr_1fr_5.5rem] gap-x-2 gap-y-1 items-center">
            <span />
            {['Start', 'Finish', 'Duration'].map(h => <span key={h} className="text-[8px] text-gray-500">{h}</span>)}
            {dateRows.map(r => (
              <Fragment key={r.label}>
                <span className="text-[9px] font-semibold text-gray-700">{r.label}</span>
                <input className={lockedLook} type="date" aria-label={`${r.label} start`} value={r.start}
                       disabled={r.lock.start} onChange={e => r.on('start', e.target.value)} />
                <input className={lockedLook} type="date" aria-label={`${r.label} finish`} value={r.finish}
                       disabled={r.lock.finish} onChange={e => r.on('finish', e.target.value)} />
                <input className={lockedLook} type="number" min="0.5" step="0.5" aria-label={`${r.label} duration`}
                       value={r.duration} placeholder={r.placeholder} disabled={r.lock.duration}
                       onChange={e => r.on('duration', e.target.value)} />
              </Fragment>
            ))}
          </div>
          <div className="text-[7.5px] text-gray-400 mt-1.5 space-y-0.5">
            {isSummary && <div>Forecast and actual dates are rolled up from the tasks underneath.</div>}
            {startNote && <div>The forecast start {startNote}.</div>}
            {!isSummary && !isMilestone && form.effort_driven && <div>The forecast duration follows the effort.</div>}
            {!isNew && !isSummary && <div>The actual dates fill in when the status changes, and can be corrected here.</div>}
          </div>
          {baselineError && <div className="text-[8px] text-red-600 mt-1">The baseline {baselineError}.</div>}
          {actualError && <div className="text-[8px] text-red-600 mt-1">The actual {actualError}.</div>}
          {clashes.map(t => (
            <div key={t.id} className="text-[8px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-1 mt-1">
              This says it started on {fmtDY(actual.start)}, before {t.name} ({t.wbs_code}){' '}
              {t.actual_finish ? `finished on ${fmtDY(t.actual_finish)}` : 'has finished'}.
              Check the actual start, or save anyway if the work really did overlap.
            </div>
          ))}
          {task?.finish_variance_days != null && (
            <div className="text-[8px] text-gray-500 mt-1">
              Forecast finish against baseline:{' '}
              <span className={`font-semibold ${task.finish_variance_days > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                {fmtVariance(task.finish_variance_days)}
              </span>
            </div>
          )}
        </div>

        {/* ── Predecessors ── */}
        {!isSummary && (
          <div className="border border-gray-200 rounded p-2">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[8px] font-bold text-gray-500 uppercase">Predecessors</span>
              <button onClick={addPredecessor} disabled={!linkable.length}
                      className="text-[8px] font-semibold px-1.5 py-0.5 rounded border border-navy/30 text-navy hover:bg-navy/5 disabled:opacity-40">
                + Add
              </button>
            </div>
            {predecessors.length === 0 ? (
              <div className="text-[8px] text-gray-400 italic">
                None. This task starts on its own date rather than following anything.
              </div>
            ) : (
              <div className="space-y-1">
                {predecessors.map((p, i) => (
                  <div key={i} className="flex items-center gap-1">
                    <select className="form-select flex-1 min-w-[120px] text-[9px]" value={p.predecessor_id}
                            onChange={e => setPred(i, { predecessor_id: e.target.value })}>
                      {linkable.map(t => (
                        <option key={t.id} value={t.id}
                                disabled={usedPredecessors.has(t.id) && t.id !== p.predecessor_id}>
                          {t.wbs_code} {t.name}
                        </option>
                      ))}
                    </select>
                    <select className="form-select w-16 text-[9px]" value={p.dep_type}
                            title={DEP_TYPES.find(d => d.id === p.dep_type)?.hint}
                            onChange={e => setPred(i, { dep_type: e.target.value })}>
                      {DEP_TYPES.map(d => <option key={d.id} value={d.id}>{d.short}</option>)}
                    </select>
                    <input className="form-input w-16 text-[9px]" style={{ width: 64, flex: '0 0 auto' }} type="number" step="0.5" value={p.lag_days}
                           title="Lag. Negative is a lead, so the task overlaps its predecessor."
                           onChange={e => setPred(i, { lag_days: parseFloat(e.target.value) || 0 })} />
                    <select className="form-select w-24 text-[9px]" value={p.lag_unit}
                            onChange={e => setPred(i, { lag_unit: e.target.value })}>
                      <option value="workdays">work days</option>
                      <option value="calendar">cal days</option>
                    </select>
                    <button onClick={() => removePred(i)} className="text-[10px] text-gray-300 hover:text-red-500 px-1">✕</button>
                  </div>
                ))}
              </div>
            )}
            <div className="text-[7.5px] text-gray-400 mt-1.5">
              {DEP_TYPES.map(d => `${d.short} ${d.hint}`).join(' · ')}. Lag can be negative for an overlap.
            </div>
          </div>
        )}

        {/* ── Team allowances on a summary bar ── */}
        {isSummary && (
          <div className="border border-gray-200 rounded p-2">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[8px] font-bold text-gray-500 uppercase">Team allowances</span>
              <button type="button" disabled={teamAllowances.length >= teams.length}
                onClick={() => setTeamAllowances(a => [...a, { team_id: teams.find(t => !a.some(x => x.team_id === t.id))?.id || '', days: '' }])}
                className="text-[8px] font-semibold px-1.5 py-0.5 rounded border border-navy/30 text-navy hover:bg-navy/5 disabled:opacity-40">
                + Team
              </button>
            </div>
            {teamAllowances.length === 0 ? (
              <div className="text-[8px] text-gray-400 italic">No teams set aside for this bar yet.</div>
            ) : (
              <div className="space-y-1">
                {teamAllowances.map((a, i) => {
                  const dup = a.team_id && teamAllowances.some((x, j) => j !== i && x.team_id === a.team_id);
                  return (
                    <div key={i} className="flex items-center gap-1">
                      <select className="form-select flex-1 min-w-[120px] text-[9px]" value={a.team_id}
                              onChange={e => setTeamAllowances(list => list.map((x, j) => (j === i ? { ...x, team_id: e.target.value } : x)))}>
                        <option value="">— team —</option>
                        {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                      <input className="form-input w-16 text-[9px]" style={{ width: 64, flex: '0 0 auto' }} type="number" min="0" step="0.5" placeholder="days" value={a.days}
                             onChange={e => setTeamAllowances(list => list.map((x, j) => (j === i ? { ...x, days: e.target.value } : x)))} />
                      <span className="text-[8px] text-gray-400">days</span>
                      <button type="button" onClick={() => setTeamAllowances(list => list.filter((_, j) => j !== i))}
                              className="text-[10px] text-gray-300 hover:text-red-500 px-1">✕</button>
                      {dup && <span className="text-[8px] text-red-600 font-semibold">Each team once</span>}
                    </div>
                  );
                })}
              </div>
            )}
            <div className="text-[7.5px] text-gray-400 mt-1.5">
              How much of each team the work inside this bar needs, before it is broken into tasks. Each team's work on the tasks
              inside draws its allowance down, staffed or not; what is left shows as unstaffed demand for the team in Resource
              Management, the unstaffed work queue and the SOW's Resourcing tab. Name people on the tasks themselves.
            </div>
          </div>
        )}

        {/* ── Who is on it ── */}
        {!isMilestone && (!isSummary || assignees.length > 0) && (
          <div className="border border-gray-200 rounded p-2">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[8px] font-bold text-gray-500 uppercase">{isSummary ? 'People on this bar (older allowances)' : 'Assigned to'}</span>
              {!isSummary && <div className="flex gap-1">
                <button onClick={() => addAssignee('person')}
                        className="text-[8px] font-semibold px-1.5 py-0.5 rounded border border-navy/30 text-navy hover:bg-navy/5">
                  + Person
                </button>
                <button onClick={() => addAssignee('party')} disabled={!externalParties.length}
                        className="text-[8px] font-semibold px-1.5 py-0.5 rounded border border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-40"
                        title={externalParties.length ? 'Add an outside organisation' : 'No external parties on file yet'}>
                  + External
                </button>
                {onCreateParty && (
                  <button onClick={onCreateParty} className="text-[8px] text-gray-400 hover:text-navy px-1">manage…</button>
                )}
              </div>}
            </div>
            {assignees.length === 0 ? (
              <div className="flex items-center gap-1 text-[8.5px] text-gray-500 flex-wrap">
                <span className="italic text-gray-400">Nobody yet.</span>
                {!isSummary && !isMilestone && (
                  <>
                    <span className="ml-2">Work needed</span>
                    <input className="form-input w-16 text-[9px]" style={{ width: 64, flex: '0 0 auto' }} type="number" min="0" step="0.5"
                           value={workDays} onChange={e => setWorkDays(e.target.value)}
                           title="Days of work this task needs from its team, until somebody is named on it" />
                    <span className="text-[8px] text-gray-400">days</span>
                    {Number(forecastDuration) > 0 && Number(workDays) > Number(forecastDuration) && (
                      <span className="text-[7.5px] text-gray-400">more than its {forecastDuration} working days, so more than one person</span>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-1">
                {assignees.map((a, i) => (
                  <div key={i} className="flex items-center gap-1">
                    {a.kind === 'person' ? (
                      <select className="form-select flex-1 min-w-[120px] text-[9px]" value={a.person_id}
                              onChange={e => setAssignee(i, { person_id: e.target.value, external_party_id: '' })}>
                        <option value="">— select person —</option>
                        {people.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
                      </select>
                    ) : (
                      <select className="form-select flex-1 min-w-[120px] text-[9px] bg-amber-50" value={a.external_party_id}
                              onChange={e => setAssignee(i, { external_party_id: e.target.value, person_id: '' })}>
                        <option value="">— select organisation —</option>
                        {externalParties.map(p => (
                          <option key={p.id} value={p.id}>{p.short_code ? `${p.short_code} — ` : ''}{p.name}</option>
                        ))}
                      </select>
                    )}
                    <input className="form-input w-16 text-[9px]" style={{ width: 64, flex: '0 0 auto' }} type="number" min="0" step="0.5"
                           value={a.days} placeholder="days" title="Days of work for them on this task, spread over their working days in its dates"
                           onChange={e => setAssignee(i, { days: e.target.value })} />
                    <span className="text-[8px] text-gray-400">days</span>
                    {(() => {
                      const share = shareOf(a, forecastStart, forecastFinish);
                      if (!(Number(a.days) > 0) || share == null) return <span className="text-[7.5px] text-gray-300 w-[92px]" />;
                      const pct = Math.round(share * 100);
                      return (
                        <span className={`text-[7.5px] w-[92px] leading-tight ${pct > 100 ? 'text-red-600 font-semibold' : 'text-gray-400'}`}
                              title={a.kind === 'person' ? 'Their days of work as a share of the working days they have in this task\'s dates' : 'As a share of the task\'s working days'}>
                          {pct > 100 ? `more than their ${r2(availableDays(a, forecastStart, forecastFinish))}d in these dates` : `≈ ${pct}% of their time`}
                        </span>
                      );
                    })()}
                    {a.kind === 'person' ? (
                      <button onClick={() => makePrimary(i)} title="The name this task reports under elsewhere in the app"
                              className={`text-[8px] px-1.5 py-0.5 rounded border ${a.is_primary
                                ? 'bg-navy text-white border-navy' : 'border-gray-200 text-gray-400 hover:bg-gray-50'}`}>
                        {a.is_primary ? 'Lead' : 'Set lead'}
                      </button>
                    ) : <span className="text-[8px] text-amber-600 px-1">external</span>}
                    <button onClick={() => removeAssignee(i)} className="text-[10px] text-gray-300 hover:text-red-500 px-1">✕</button>
                  </div>
                ))}
              </div>
            )}
            <div className="text-[7.5px] text-gray-400 mt-1.5">
              {isSummary ? (
                <>
                  These people were on this bar before it was a summary, or before summary bars took team allowances. Their time
                  still counts: the time they have on the tasks inside draws it down, and whatever is left shows as "not yet on a
                  task inside". Remove them once their time is on tasks or in a team allowance above.
                </>
              ) : (
                <>
                  Give each person their days of work on this task. They are spread over that person's own working days in the
                  task's dates, so three days for somebody who works Monday, Wednesday and Friday lands on those days. The share
                  beside each is what that comes to. The lead is who the task shows against in Task Oversight, Resource
                  Management and the Planner.
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Split between teams ── */}
        {!isNew && !isSummary && !isMilestone && onSplit && (
          <SplitByTeam task={task} teams={teams} onSplit={onSplit} />
        )}

        {/* ── Checklist ── */}
        {!isSummary && !isMilestone && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Task type</label>
              <select className="form-select" value={form.task_type} onChange={e => set('task_type', e.target.value)}>
                <option value="custom">Custom</option>
                <option value="checklist">Checklist</option>
              </select>
            </div>
            {form.task_type === 'checklist' && (
              <div>
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Checklist template *</label>
                <select className="form-select" value={form.checklist_template_id}
                        onChange={e => set('checklist_template_id', e.target.value)}>
                  <option value="">Select…</option>
                  {checklistTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

/*
  Split a task between teams. It becomes a summary bar with one task per team
  underneath, each with its share of the work over the same dates. People move
  to the part for their own team; anybody else stays on the bar as an allowance.
  The result, or what is wrong, shows in the box.
*/
function SplitByTeam({ task, teams = [], onSplit }) {
  const [open, setOpen] = useState(false);
  const first = task?.team_id || teams[0]?.id || '';
  const second = teams.find(t => t.id !== first)?.id || '';
  const [parts, setParts] = useState([{ team_id: first, share: 50 }, { team_id: second, share: 50 }]);
  const [state, setState] = useState(null);   // null | 'saving' | { error }

  const total = parts.reduce((s, p) => s + (Number(p.share) || 0), 0);
  const distinct = new Set(parts.map(p => p.team_id).filter(Boolean)).size === parts.length;
  const ok = parts.length >= 2 && distinct && parts.every(p => p.team_id && Number(p.share) > 0) && Math.abs(total - 100) <= 0.5;
  const setPart = (i, patch) => setParts(ps => ps.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const even = (list) => {
    const n = list.length;
    const base = Math.floor(100 / n);
    return list.map((p, i) => ({ ...p, share: i === n - 1 ? 100 - base * (n - 1) : base }));
  };

  const split = async () => {
    setState('saving');
    try { await onSplit(parts.map(p => ({ team_id: p.team_id, share: Number(p.share) }))); }
    catch (e) { setState({ error: e.message || 'Could not split the task.' }); }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} disabled={teams.length < 2}
        className="text-[8.5px] font-semibold text-navy hover:underline disabled:opacity-40 text-left">
        Split this task between teams…
      </button>
    );
  }
  return (
    <div className="border border-navy/20 bg-navy/5 rounded p-2 space-y-1.5">
      <div className="text-[8px] font-bold text-navy uppercase">Split between teams</div>
      <div className="text-[7.5px] text-gray-500">
        This task becomes a summary bar with one task per team inside it, each over the same dates with its share of the work.
        People move to their own team's task; anybody else stays on the bar as an allowance.
      </div>
      {parts.map((p, i) => (
        <div key={i} className="flex items-center gap-1">
          <select className="form-select flex-1 min-w-[120px] text-[9px]" value={p.team_id} onChange={e => setPart(i, { team_id: e.target.value })}>
            <option value="">— team —</option>
            {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <input className="form-input w-14 text-[9px]" style={{ width: 56, flex: '0 0 auto' }} type="number" min="1" max="99" value={p.share}
                 onChange={e => setPart(i, { share: e.target.value })} />
          <span className="text-[8px] text-gray-400">%</span>
          {parts.length > 2 && (
            <button type="button" onClick={() => setParts(ps => even(ps.filter((_, j) => j !== i)))} className="text-[10px] text-gray-300 hover:text-red-500 px-1">✕</button>
          )}
        </div>
      ))}
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" onClick={() => setParts(ps => even([...ps, { team_id: teams.find(t => !ps.some(p => p.team_id === t.id))?.id || '', share: 0 }]))}
          disabled={parts.length >= teams.length} className="text-[8px] text-navy hover:underline disabled:opacity-40">+ Another team</button>
        <button type="button" onClick={() => setParts(ps => even(ps))} className="text-[8px] text-gray-500 hover:underline">Share evenly</button>
        <span className={`text-[8px] ${Math.abs(total - 100) <= 0.5 ? 'text-gray-400' : 'text-red-600 font-semibold'}`}>{Math.round(total)}% of the work</span>
        {!distinct && <span className="text-[8px] text-red-600 font-semibold">Each team once</span>}
        <span className="ml-auto flex gap-1">
          <button type="button" onClick={() => { setOpen(false); setState(null); }} className="px-2 py-0.5 text-[8.5px] border border-gray-200 rounded bg-white">Keep as one task</button>
          <button type="button" onClick={split} disabled={!ok || state === 'saving'}
            className="px-2 py-0.5 text-[8.5px] font-semibold rounded bg-navy text-white disabled:opacity-40">
            {state === 'saving' ? 'Splitting…' : `Split into ${parts.length} tasks`}
          </button>
        </span>
      </div>
      {state?.error && <div className="text-[8px] text-red-700">{state.error}</div>}
    </div>
  );
}
